import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type EventType = 
  | 'button_click' 
  | 'feature_use' 
  | 'navigation' 
  | 'form_submit' 
  | 'error'
  | 'assessment_action'
  | 'export'
  | 'profile_action';

interface TrackEventParams {
  eventType: EventType;
  eventName: string;
  metadata?: Record<string, any>;
  sessionId?: string | null;
}

export const useEventTracking = (sessionId?: string | null) => {
  const { user } = useAuth();
  const location = useLocation();

  const trackEvent = useCallback(async ({
    eventType,
    eventName,
    metadata = {},
    sessionId: customSessionId
  }: TrackEventParams) => {
    if (!user?.id) return;

    try {
      // Send to GA4
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', eventName, {
          event_category: eventType,
          page_path: location.pathname,
          ...metadata
        });
      }

      await supabase
        .from('user_events')
        .insert({
          user_id: user.id,
          session_id: customSessionId || sessionId,
          event_type: eventType,
          event_name: eventName,
          page_path: location.pathname,
          metadata: metadata
        });

      console.log('[Event Tracking] Event tracked:', { eventType, eventName, metadata });
    } catch (error) {
      console.error('[Event Tracking] Error tracking event:', error);
    }
  }, [user?.id, sessionId, location.pathname]);

  // Convenience methods for common events
  const trackButtonClick = useCallback((buttonName: string, metadata?: Record<string, any>) => {
    trackEvent({
      eventType: 'button_click',
      eventName: `button_${buttonName}`,
      metadata
    });
  }, [trackEvent]);

  const trackFeatureUse = useCallback((featureName: string, metadata?: Record<string, any>) => {
    trackEvent({
      eventType: 'feature_use',
      eventName: `feature_${featureName}`,
      metadata
    });
  }, [trackEvent]);

  const trackFormSubmit = useCallback((formName: string, metadata?: Record<string, any>) => {
    trackEvent({
      eventType: 'form_submit',
      eventName: `form_${formName}`,
      metadata
    });
  }, [trackEvent]);

  const trackNavigation = useCallback((destination: string, metadata?: Record<string, any>) => {
    trackEvent({
      eventType: 'navigation',
      eventName: `nav_to_${destination}`,
      metadata
    });
  }, [trackEvent]);

  const trackAssessmentAction = useCallback((action: string, metadata?: Record<string, any>) => {
    trackEvent({
      eventType: 'assessment_action',
      eventName: `assessment_${action}`,
      metadata
    });
  }, [trackEvent]);

  const trackExport = useCallback((exportType: string, metadata?: Record<string, any>) => {
    trackEvent({
      eventType: 'export',
      eventName: `export_${exportType}`,
      metadata
    });
  }, [trackEvent]);

  const trackProfileAction = useCallback((action: string, metadata?: Record<string, any>) => {
    trackEvent({
      eventType: 'profile_action',
      eventName: `profile_${action}`,
      metadata
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackButtonClick,
    trackFeatureUse,
    trackFormSubmit,
    trackNavigation,
    trackAssessmentAction,
    trackExport,
    trackProfileAction
  };
};
